import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useGetInventorySheetQuery,
  useSubmitInventoryCountMutation,
} from '../../redux/api/inventoryApi';
import { useApiErrorToast } from '../../hooks/useApiError';

const keyFor = (source, articleId) => `${source}:${articleId}`;

const DiffCell = ({ countedStr, soll }) => {
  if (countedStr === undefined || countedStr === '') {
    return <td className='text-muted'>—</td>;
  }
  const diff = (parseInt(countedStr, 10) || 0) - soll;
  return (
    <td className={diff !== 0 ? 'table-warning fw-bold' : ''}>
      {diff > 0 ? `+${diff}` : diff}
    </td>
  );
};

const InventoryCountSheet = () => {
  const curEvent = useSelector((state) => state.auth.currentEvent);
  const navigate = useNavigate();
  const apiErrorToast = useApiErrorToast();

  const { data: sheet, isLoading } = useGetInventorySheetQuery(curEvent?.id, {
    skip: !curEvent?.id,
  });
  const [submit, { isLoading: isSubmitting }] =
    useSubmitInventoryCountMutation();

  const [counted, setCounted] = useState({});
  const [note, setNote] = useState('');

  const setCount = (source, articleId, value) =>
    setCounted((prev) => ({ ...prev, [keyFor(source, articleId)]: value }));

  if (!curEvent?.id) {
    return <p className='mt-4'>Bitte zuerst oben ein Event auswählen.</p>;
  }
  if (isLoading) {
    return <p className='mt-4'>Zählliste wird geladen...</p>;
  }
  if (!sheet) {
    return <p className='mt-4'>Keine Daten gefunden.</p>;
  }

  const { storages, rows, markt_enabled: marktEnabled } = sheet;
  // Hide rows whose system Soll is 0 — nothing to count there.
  const visibleRows = rows.filter((row) => row.should_be_total !== 0);
  // Artikel, Gebinde, …storages, [Markt], Soll, Gezählt, Differenz
  const colCount = storages.length + (marktEnabled ? 6 : 5);

  const buildItems = () =>
    rows
      .filter((row) => {
        const c = counted[keyFor(row.source, row.article_id)];
        return c !== undefined && c !== '';
      })
      .map((row) => ({
        source: row.source,
        article_id: row.article_id,
        counted_quantity:
          parseInt(counted[keyFor(row.source, row.article_id)], 10) || 0,
      }));

  const handleSubmit = async () => {
    const items = buildItems();
    if (items.length === 0) {
      toast.warn('Bitte mindestens einen Zählwert eingeben.');
      return;
    }
    try {
      await submit({ event: curEvent.id, note: note.trim(), items }).unwrap();
      toast.success('Inventur gespeichert!');
      navigate('/inventory-counts');
    } catch (err) {
      apiErrorToast(err, 'Fehler beim Speichern der Inventur');
    }
  };

  return (
    <div>
      <h4 className='mt-2'>Inventur zählen</h4>
      <p className='text-muted'>
        {marktEnabled ? (
          <>
            Lager und Markt werden gemeinsam gezählt. Verknüpfte Artikel
            erscheinen als <strong>eine</strong> Zeile (Soll = Lager + Markt);
            trage
          </>
        ) : (
          <>
            Für dieses Event ist der Getränkemarkt deaktiviert – es wird nur das
            Lager gezählt. Trage
          </>
        )}{' '}
        je Zeile die gezählte Menge ein – die Differenz zum Soll wird
        automatisch berechnet. Es werden <strong>keine</strong>{' '}
        Bestandskorrekturen gebucht; die Zählung wird nur protokolliert.
      </p>

      <div className='row mt-3'>
        <div className='col-2'>
          <label htmlFor='note' className='col-form-label'>
            Notiz
          </label>
        </div>
        <div className='col-6'>
          <input
            id='note'
            type='text'
            className='form-control'
            maxLength={500}
            placeholder='z.B. Abendzählung, Zähler: Max'
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>

      <table className='table table-striped mt-4 align-middle text-start'>
        <thead>
          <tr>
            <th>Artikel</th>
            <th>Gebinde</th>
            {storages.map((s) => (
              <th key={s.id} className='text-end'>
                {s.name}
              </th>
            ))}
            {marktEnabled && <th className='text-end'>Markt</th>}
            <th className='text-end'>Soll</th>
            <th>Gezählt</th>
            <th>Differenz</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className='text-muted'>
                Keine Artikel mit Bestand.
              </td>
            </tr>
          ) : (
            visibleRows.map((row) => {
              const ck = keyFor(row.source, row.article_id);
              return (
                <tr key={ck}>
                  <td>{row.article_name}</td>
                  <td>{row.unit}</td>
                  {storages.map((s) => (
                    <td key={s.id} className='text-end'>
                      {row.per_storage[s.id] ?? '—'}
                    </td>
                  ))}
                  {marktEnabled && (
                    <td className='text-end'>{row.markt ?? '—'}</td>
                  )}
                  <td className='text-end'>
                    <strong>{row.should_be_total}</strong>
                  </td>
                  <td>
                    <input
                      type='number'
                      min={0}
                      className='form-control'
                      style={{ maxWidth: '7rem' }}
                      value={counted[ck] ?? ''}
                      onChange={(e) =>
                        setCount(row.source, row.article_id, e.target.value)
                      }
                    />
                  </td>
                  <DiffCell
                    countedStr={counted[ck]}
                    soll={row.should_be_total}
                  />
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <button
        className='btn btn-primary'
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        Inventur speichern
      </button>
    </div>
  );
};

export default InventoryCountSheet;
